import React, { useMemo } from 'react';
import { Input, Select, Tooltip, Space } from 'antd';
import { QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import EnlargeableInputTerms from 'components/commons/IssueCrashFilter/EnlargeableInputTerms';
import { ze } from 'utils/zhEn';
import WrappedTipsIcon from 'components/antd-extension/WrappedTipsIcon.jsx';

// 定义搜索方法枚举
const SearchMethod = Object.freeze({
  ALL_MATCH: 'ALL_MATCH', // 全命中
  NOT_ALL_MATCH: 'NOT_ALL_MATCH', // 非全命中
  REGEXP_MATCH: 'REGEXP_MATCH', // 正则表达式
  NOT_REGEXP_MATCH: 'NOT_REGEXP_MATCH', // 正则不匹配
});

// 搜索方法到queryType和not的映射
const SearchMethodToInfo = {
  [SearchMethod.ALL_MATCH]: {
    queryType: QueryType.TERMS_SET_ALL_MATCH,
    not: false,
    isTermsLike: true,
  },
  [SearchMethod.NOT_ALL_MATCH]: {
    queryType: QueryType.TERMS_SET_ALL_MATCH,
    not: true,
    isTermsLike: true,
  },
  [SearchMethod.REGEXP_MATCH]: {
    queryType: QueryType.TERM_REGEXP_MATCH,
    not: false,
    isInput: true,
  },
  [SearchMethod.NOT_REGEXP_MATCH]: {
    queryType: QueryType.TERM_REGEXP_MATCH,
    not: true,
    isInput: true,
  },
};

// 根据queryType和not查找对应的搜索方法
function findSearchMethod(queryType, not) {
  // 兼容旧数据：将CUSTOM转换为TERMS_SET_ALL_MATCH
  const normalizedQueryType = queryType === QueryType.CUSTOM ? QueryType.TERMS_SET_ALL_MATCH : queryType;
  
  const foundEntry = Object.entries(SearchMethodToInfo).find(([k, v]) => {
    return v.queryType === normalizedQueryType && v.not === Boolean(not);
  });
  return foundEntry ? foundEntry[0] : null;
}

// 创建带提示的标签
function makeLabelWithTooltip(label, tooltipContent) {
  if (!tooltipContent) {
    return label;
  }
  return <Tooltip
    placement='right'
    title={tooltipContent}
  >
    <Space>
      <div>{ label }</div>
      <WrappedTipsIcon />
    </Space>
  </Tooltip>;
}

// 搜索方法选项配置
const SEARCH_METHOD_OPTIONS = [
  { 
    name: '全命中', 
    value: SearchMethod.ALL_MATCH,
  },
  { 
    name: '非全命中', 
    value: SearchMethod.NOT_ALL_MATCH,
  },
  {
    name: ze('正则表达式', 'RegExp'),
    value: SearchMethod.REGEXP_MATCH,
    description: ze(
      '指定的正则表达式必须能匹配到被搜索字段的部分内容。如果需要完全匹配，在表达式中使用锚点符号（^和$）。',
      'The specified regular expression must match part of the content in the search field. If you need an exact match, use anchors (^ and $) in the expression.'
    ),
  },
  {
    name: ze('正则不匹配', 'RegExp Not Match'),
    value: SearchMethod.NOT_REGEXP_MATCH,
    description: ze(
      '指定的正则表达式不能匹配到被搜索字段的部分内容。如果需要不能匹配到完整内容，在表达式中使用锚点符号（^和$）。',
      'The specified regular expression must NOT match part of the content in the search field. If you need an exact NOT match, use anchors (^ and $) in the expression.'
    ),
  },
].map(x => ({
  ...x,
  label: makeLabelWithTooltip(x.name, x.description),
}));

/**
 * appSoNameWhitelist字段的搜索组件
 * 支持四种搜索方式：
 * 1. 全命中 - 所有so都要在查询条件中
 * 2. 非全命中 - 不是所有so都在查询条件中
 * 3. 正则表达式 - 使用正则表达式匹配
 * 4. 正则不匹配 - 使用正则表达式不匹配
 */
const AppSoNameWhitelistSearch = ({
  fieldValue,
  onChangeFieldValue = () => {},
  isReadOnly,
}) => {
  const {
    queryType: nullableQueryType,
    not,
    term,
    terms,
  } = fieldValue || {};

  // 兼容旧数据：将CUSTOM转换为TERMS_SET_ALL_MATCH
  const queryType = nullableQueryType === QueryType.CUSTOM 
    ? QueryType.TERMS_SET_ALL_MATCH 
    : (nullableQueryType || QueryType.TERMS_SET_ALL_MATCH);

  const searchMethodValue = findSearchMethod(queryType, not);
  const { isInput, isTermsLike } = SearchMethodToInfo[searchMethodValue] || {};

  return <div style={{ display: 'flex' }}>
    <Select
      style={{ width: '120px', flex: '0 0 auto' }}
      options={SEARCH_METHOD_OPTIONS}
      optionFilterProp='name'
      optionLabelProp='name'
      value={ searchMethodValue }
      onChange={(v) => {
        if (isReadOnly) {
          return;
        }
        const newInfo = SearchMethodToInfo[v];
        onChangeFieldValue({ 
          queryType: newInfo.queryType, 
          not: newInfo.not,
        });
      }}
    />
    { isTermsLike && <EnlargeableInputTerms
      style={{ flexGrow: 1, fontSize: '14px' }}
      value={terms}
      placeholder='大小写敏感，用逗号分隔多个so'
      description='大小写敏感，用逗号分隔多个so。'
      onChange={v => {
        if (isReadOnly) {
          return;
        }
        onChangeFieldValue({ 
          queryType, 
          not, 
          terms: v,
        });
      }}
    /> }
    { isInput && <Input
      style={{ flexGrow: 1 }}
      placeholder={ze('正则表达式', 'Regular Expression')}
      value={ term }
      onChange={(e) => {
        if (isReadOnly) {
          return;
        }
        const v = e.target.value;
        onChangeFieldValue({ 
          queryType, 
          not, 
          term: v,
        });
      }}
    /> }
  </div>;
};

export default AppSoNameWhitelistSearch;

